import { ClassNames } from '@emotion/react';
import { Box, Stack, Table, TableBody, TableCell, TableContainer, TableRow, Typography } from '@mui/material';
import { Popup } from '@vis.gl/react-maplibre';
import dayjs from 'dayjs';
import { Fragment } from 'react';

import { PopupContent } from '@/components/PopupContent';
import { SmallTypography } from '@/components/popup/TrafficReportPopup';
import type { ParkingFeature } from '@/types/ParkingFeature';

interface ParkingPopupProps {
  feature?: ParkingFeature;
  onClose: () => void;
}

export function ParkingPopup({ feature, onClose }: ParkingPopupProps) {
  if (!feature) {
    return null;
  }

  const [longitude, latitude] = feature.geometry.coordinates;

  const { name } = feature.properties;
  const parkingFacilityId = feature.properties.parking_facility_id;

  return (
    <ClassNames>
      {({ css: cssClassName }) => (
        <Popup
          key={parkingFacilityId}
          anchor="bottom-right"
          closeButton={false}
          closeOnClick={false}
          latitude={latitude}
          longitude={longitude}
          maxWidth="294px"
          offset={24}
          onClose={onClose}
          className={cssClassName`
            .maplibregl-popup-content {
              border-radius: 8px 8px 8px 0;
              width: 294px;
            }
          `}
        >
          <PopupContent onClose={onClose}>
            <Stack spacing={2}>
              <Stack direction="column">
                <Typography variant="h6">{name}</Typography>
                <SmallTypography color="text.secondary" variant="body2">
                  ID: {parkingFacilityId}
                </SmallTypography>
              </Stack>

              <Stack direction="column">
                <Stack direction="row" spacing={1}>
                  <Typography variant="subtitle2">Datum</Typography>
                  <Typography variant="body2">{dayjs().format('DD.MM.YYYY')}</Typography>
                </Stack>

                {typeof feature.properties.num_parking_space_total === 'number' && (
                  <Box>
                    {feature.properties.isLive && typeof feature.properties.short_term_parking_free === 'number' ? (
                      <Fragment>
                        <Typography color="success.main" component="span" fontWeight={500} variant="body2">
                          {feature.properties.short_term_parking_free}
                        </Typography>{' '}
                        <Typography component="span" variant="body2">
                          /
                        </Typography>{' '}
                      </Fragment>
                    ) : null}
                    <Typography component="span" fontWeight={500} variant="body2">
                      {feature.properties.num_parking_space_total}
                    </Typography>{' '}
                    <Typography component="span" variant="body2">
                      verfügbar
                    </Typography>
                  </Box>
                )}
              </Stack>

              <Box>
                <Typography variant="subtitle2">Meldungen</Typography>
                <TableContainer sx={{ width: 98 }}>
                  <Table size="small" sx={{ '& td': { border: 0, padding: 0 } }}>
                    <TableBody>
                      {feature.properties.num_parking_space_disabled ? (
                        <TableRow>
                          <TableCell>
                            <Typography variant="body2">Behinderte</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography fontWeight={500} variant="body2">
                              {feature.properties.num_parking_space_disabled}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : null}
                      {feature.properties.num_parking_space_short_term ? (
                        <TableRow>
                          <TableCell>
                            <Typography variant="body2">Kurzzeit</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography fontWeight={500} variant="body2">
                              {feature.properties.num_parking_space_short_term}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : null}
                      {feature.properties.num_parking_space_women ? (
                        <TableRow>
                          <TableCell>
                            <Typography variant="body2">Frauen</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography fontWeight={500} variant="body2">
                              {feature.properties.num_parking_space_women}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : null}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Stack>
          </PopupContent>
        </Popup>
      )}
    </ClassNames>
  );
}
